// Comprehensive validation utilities for authentication

export const validateEmail = (email) => {
  if (!email?.trim()) return 'Email is required';
  
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!emailRegex.test(email.trim())) return 'Please enter a valid email address';
  
  if (email.length > 254) return 'Email is too long';
  
  return '';
};

export const validatePassword = (password) => {
  if (!password) return 'Password is required';
  
  const minLength = 8;
  const maxLength = 128;
  
  if (password.length < minLength) return `Password must be at least ${minLength} characters`;
  if (password.length > maxLength) return `Password must be less than ${maxLength} characters`;
  
  const requirements = [
    { regex: /[a-z]/, message: 'lowercase letter' },
    { regex: /[A-Z]/, message: 'uppercase letter' },
    { regex: /[0-9]/, message: 'number' },
    { regex: /[^A-Za-z0-9]/, message: 'special character' }
  ];
  
  const missing = requirements.filter(req => !req.regex.test(password));
  if (missing.length > 0) {
    return `Password must include: ${missing.map(m => m.message).join(', ')}`;
  }
  
  // Check for common weak patterns
  const weakPatterns = [
    /^(.)\1+$/, // All same character
    /^(012|123|234|345|456|567|678|789|890|abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz)/i,
    /^(password|123456|qwerty|admin|login|welcome)/i
  ];
  
  if (weakPatterns.some(pattern => pattern.test(password))) {
    return 'Password is too common or predictable';
  }
  
  return '';
};

export const validateDisplayName = (name) => {
  if (!name?.trim()) return 'Display name is required';
  
  const trimmed = name.trim();
  if (trimmed.length < 2) return 'Display name must be at least 2 characters';
  if (trimmed.length > 50) return 'Display name must be less than 50 characters';
  
  // Check for inappropriate content (basic)
  const inappropriate = /\b(admin|root|system|null|undefined|test|demo)\b/i;
  if (inappropriate.test(trimmed)) return 'Display name contains restricted words';
  
  // Check for valid characters (letters, numbers, spaces, basic punctuation)
  const validChars = /^[a-zA-Z0-9\s\-_.]+$/;
  if (!validChars.test(trimmed)) return 'Display name contains invalid characters';
  
  return '';
};

export const validatePhoneNumber = (phone) => {
  if (!phone?.trim()) return ''; // Optional field
  
  const cleaned = phone.replace(/[\s\-\(\)]/g, '');
  
  // Indian mobile number validation
  const indianMobile = /^[6-9]\d{9}$/;
  if (!indianMobile.test(cleaned)) {
    return 'Please enter a valid 10-digit Indian mobile number';
  }
  
  return '';
};

export const validateConfirmationCode = (code) => {
  if (!code?.trim()) return 'Confirmation code is required';
  
  const cleaned = code.replace(/\s/g, '');
  if (!/^\d{6}$/.test(cleaned)) return 'Confirmation code must be 6 digits';
  
  return '';
};

export const calculatePasswordStrength = (password) => {
  if (!password) return { score: 0, feedback: [] };
  
  let score = 0;
  const feedback = [];
  
  // Length scoring
  if (password.length >= 8) score += 20;
  else feedback.push('Use at least 8 characters');
  
  if (password.length >= 12) score += 10;
  
  // Character variety
  if (/[a-z]/.test(password)) score += 15;
  else feedback.push('Add lowercase letters');
  
  if (/[A-Z]/.test(password)) score += 15;
  else feedback.push('Add uppercase letters');
  
  if (/[0-9]/.test(password)) score += 15;
  else feedback.push('Add numbers');
  
  if (/[^A-Za-z0-9]/.test(password)) score += 15;
  else feedback.push('Add special characters');
  
  // Bonus points for complexity
  const uniqueChars = new Set(password).size;
  if (uniqueChars >= password.length * 0.7) score += 10;
  
  return {
    score: Math.min(score, 100),
    feedback: feedback.slice(0, 2) // Limit feedback items
  };
};

export const getPasswordStrengthColor = (score) => {
  if (score < 40) return 'error';
  if (score < 70) return 'warning';
  return 'success';
};

export const getPasswordStrengthText = (score) => {
  if (score < 40) return 'Weak';
  if (score < 70) return 'Medium';
  return 'Strong';
};

// Rate limiting for form submissions
const submissionTimes = new Map();

export const checkRateLimit = (identifier, maxAttempts = 5, windowMs = 300000) => {
  const now = Date.now();
  const key = `auth_${identifier}`;
  
  if (!submissionTimes.has(key)) {
    submissionTimes.set(key, []);
  }
  
  const attempts = submissionTimes.get(key);
  
  // Remove old attempts outside the window
  const validAttempts = attempts.filter(time => now - time < windowMs);
  submissionTimes.set(key, validAttempts);
  
  if (validAttempts.length >= maxAttempts) {
    const oldestAttempt = Math.min(...validAttempts);
    const waitTime = Math.ceil((windowMs - (now - oldestAttempt)) / 1000);
    return { allowed: false, waitTime };
  }
  
  // Record this attempt
  validAttempts.push(now);
  submissionTimes.set(key, validAttempts);
  
  return { allowed: true, waitTime: 0 };
};

// Security headers and CSP helpers
export const getSecurityHeaders = () => ({
  'X-Content-Type-Options': 'nosniff',
  'X-Frame-Options': 'DENY',
  'X-XSS-Protection': '1; mode=block',
  'Referrer-Policy': 'strict-origin-when-cross-origin'
});

// Input sanitization
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  return input
    .trim()
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .slice(0, 1000); // Limit length
};